import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { NestedRows } from 'modules/finance/components/FinancialWorkspace/components/Table/table/NestedRows'
import { useCachedData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/CachedDataProvider'
import { HookData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/types/data'

import { CustomChildrenProps } from './props'

type CustomProps = Record<string, unknown>

interface BaseProps extends CustomChildrenProps {
  type: 'expenses' | 'agreements' | 'payroll'
  hook: (props: CustomProps) => HookData
  customProps?: CustomProps
}

export const Base = ({
  id,
  type,
  hook,
  level,
  groupBy,
  customProps,
  headerLength,
  tableHeadRef,
  parentHasItemWithChildren,
  setParentHasItemWithChildren,
}: BaseProps) => {
  const { data, setData } = useCachedData()

  const key = `${type}-${groupBy}-${id}`

  const storedData = data[key]

  const hookData: HookData = hook({
    skip: !!storedData,
    resource: { type: groupBy, id: Number(id) },
    ...customProps,
  })

  const filteredData = useUnsafeMemo(
    () => storedData?.data ?? hookData?.data?.filter(Boolean) ?? [],
    [hookData?.data, storedData?.data]
  )

  useUnsafeEffect(() => {
    if (!storedData && hookData && !hookData.loading) {
      setData({ ...data, [key]: hookData })
    }
  }, [hookData, storedData, data, setData, key])

  useUnsafeEffect(() => {
    if (
      filteredData.some((item) => {
        return !!(
          item.children?.length ||
          item.customChildren?.length ||
          (!!item.onLazyChildren && item.data.some((i) => Number(i.totalAmountInCents) > 0))
        )
      })
    ) {
      setParentHasItemWithChildren?.(true)
    }
  }, [filteredData, setParentHasItemWithChildren])

  if (!filteredData || !hookData?.visible) {
    return <></>
  }

  return (
    <NestedRows
      level={level}
      groupBy={groupBy}
      items={filteredData}
      currentPageCount={0}
      headerCount={headerLength}
      tableHeadRef={tableHeadRef}
      parentHasItemWithChildren={parentHasItemWithChildren}
    />
  )
}
